import { HELP_ELEMENT_CLASS_NAME } from '@/data/constants/layout';
import { ThemeOptions } from '@mui/material/styles';

// Local-only: routes font requests through the same-origin /local-cdn-proxy/fonts
// rewrite (see next.config.js) instead of the cross-origin CDN, avoiding CORS
// errors when running `yarn start` locally. See
// doc/developer-guide/local-remote-proxy.md. NEXT_PUBLIC_LOCAL_FONT_PROXY is
// unset in every real environment, so FONT_CDN_BASE resolves to the real CDN there.
const FONT_CDN_BASE =
	process.env.NEXT_PUBLIC_LOCAL_FONT_PROXY === 'true'
		? '/local-cdn-proxy/fonts'
		: 'https://cdn.homedepot.com.mx/assets/fonts';

const openSansLight = {
	family: 'Open Sans',
	style: 'normal',
	weight: 300,
	display: 'swap',
	src: `
  local("Open Sans Light"),
  local("OpenSans-Light"),
  url("${FONT_CDN_BASE}/Open_Sans/OpenSans-Light.woff2") 
  format("woff2")
  `,
	unicodeRange: undefined,
};

const openSansRegular = {
	family: 'Open Sans',
	style: 'normal',
	weight: 400,
	display: 'swap',
	src: `
  local("Open Sans Regular"),
  local("OpenSans-Regular"),
  url("${FONT_CDN_BASE}/Open_Sans/OpenSans-Regular.woff2") 
  format("woff2")
  `,
	unicodeRange: undefined,
};

const openSansSemiBold = {
	family: 'Open Sans',
	style: 'normal',
	weight: 600,
	display: 'swap',
	src: `
  local("Open Sans SemiBold"),
  local("OpenSans-SemiBold"),
  url("${FONT_CDN_BASE}/Open_Sans/OpenSans-SemiBold.woff2") 
  format("woff2")
  `,
	unicodeRange: undefined,
};

const openSansBold = {
	family: 'Open Sans',
	style: 'normal',
	weight: 700,
	display: 'swap',
	src: `
  local("Open Sans Bold"),
  local("OpenSans-Bold"),
  url("${FONT_CDN_BASE}/Open_Sans/OpenSans-Bold.woff2") 
  format("woff2")
  `,
	unicodeRange: undefined,
};

const helveticaNeueTHDNormal = {
	family: 'HelveticaNeue_THD',
	style: 'normal',
	weight: 400,
	display: 'swap',
	src: `
  local("Helvetica Neue LT Std"),
  url("${FONT_CDN_BASE}/HelveticaNeueLTStd-HvCn.woff2") 
  format("woff2")
  `,
	unicodeRange: undefined,
};

const helveticaNeueTHDBold = {
	family: 'HelveticaNeue_THD',
	style: 'normal',
	weight: 600,
	display: 'swap',
	src: `
  local("Helvetica Neue LT Std"),
  url("${FONT_CDN_BASE}/HelveticaNeueLTStd-BlkCn.woff2") 
  format("woff2")
  `,
	unicodeRange: undefined,
};

const futuraRegular = {
	family: 'Futura',
	style: 'normal',
	weight: 400,
	display: 'swap',
	src: `
  local("Futura"),
  url("${FONT_CDN_BASE}/Futura.ttf")
  format("ttf")
  `,
	unicodeRange: undefined,
};

const futuraBold = {
	family: 'Futura',
	style: 'normal',
	weight: 600,
	display: 'swap',
	src: `
  local("Futura"),
  url("${FONT_CDN_BASE}/Futura.ttf")
  format("ttf")
  `,
	unicodeRange: undefined,
};

export const fontFaceHDM = [
	openSansLight,
	openSansRegular,
	openSansSemiBold,
	openSansBold,
	helveticaNeueTHDNormal,
	helveticaNeueTHDBold,
	futuraRegular,
	futuraBold,
];

export const CssBaseline: ThemeOptions = {
	components: {
		MuiCssBaseline: {
			styleOverrides: () => `
      ${fontFaceHDM
				.map(
					({ family, style, display, weight, src, unicodeRange }) => `
            @font-face {
              font-family: ${family};
              font-style: ${style};
              font-display: ${display};
              font-weight: ${weight};
              src: ${src};
              ${unicodeRange ? `unicodeRange: ${unicodeRange};` : ''}
            }
            `
				)
				.join(``)}


      sup, sub {
				font-size: 54%;
				margin-left: 3px;
				position: relative;
      }

			sup {
				vertical-align: top;
				top: -0.4em;
			}

			sub {
				vertical-align: bottom;
				bottom: -0.2em;
			}

			[style*="font-family: HelveticaNeue_THD"] sup,
			[style*="font-family: HelveticaNeue_THD"] sub {
				font-family: HelveticaNeue_THD, sans-serif;
			}

			[style*="font-family: Open Sans"] sup,
			[style*="font-family: Open Sans"] sub {
				font-family: Open Sans, sans-serif;
			}


      body { overflow-x: hidden; }
      html, body, #__next { max-width: 100vw; min-height: 100vh; display: flex; flex: 1; }
			#__next { flex-direction: column; }
			#__next > main {flex: 1;}
			img { max-width: 100%; }

			body > .initial-opacity.${HELP_ELEMENT_CLASS_NAME} {
				width: auto !important;
			}
			`,
		},
		MuiContainer: {
			styleOverrides: {
				root: {
					padding: '0px !important',
				},
			},
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					backgroundColor: '#ffffff',
					boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.2)',
					'@media (max-width:799px)': {
						padding: '0',
					},
					'@media (min-width:800px)': {
						padding: '0',
					},
				},
			},
		},
		MuiAccordionSummary: {
			styleOverrides: {
				content: {
					color: '#333333',
					fontSize: 20,
					fontWeight: 400,
					lineHeight: 1.2,
					'@media (max-width:799px)': {
						padding: '10px',
					},
					'@media (min-width:800px)': {
						padding: '20px',
					},
				},
				expandIconWrapper: {
					['&&']: {
						'@media (max-width:799px)': {
							padding: '10px',
						},
						'@media (min-width:800px)': {
							padding: '20px',
						},
					},
				},
			},
		},
		MuiAccordionDetails: {
			styleOverrides: {
				root: {
					color: '#333333',
					fontSize: 15,
					fontWeight: 400,
					lineHeight: 1.2,
					marginTop: '20px',
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					height: 40,
					marginTop: '10px',
					'& fieldset': {
						borderColor: '#999999',
					},
				},
				input: {
					padding: '10px',
					'&.Mui-disabled': {
						backgroundColor: '#c8c7c842',
					},
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				input: {
					padding: '10px',
					'&.Mui-disabled': {
						WebkitTextFillColor: '#999999',
					},
				},
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					'&.Mui-focused': {
						color: '#a9a9a9',
					},
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						backgroundColor: '#c8c7c842',
					},
					color: '#a9a9a9',
				},
				icon: { color: '#fd8331' },
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					marginTop: '5px',
				},
			},
		},
	},
};
